/**
 * RoomsManagement - Manage accommodation rooms
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  BedDouble, Plus, Edit2, Trash2, CheckCircle, Users, 
  AlertTriangle, Wrench, Ban, Filter, Search
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROOM_TYPES = [
  { value: 'single', label: 'Single', capacity: 1 },
  { value: 'double', label: 'Double', capacity: 2 },
  { value: 'triple', label: 'Triple', capacity: 3 },
  { value: 'quad', label: 'Quad', capacity: 4 },
  { value: 'dorm', label: 'Dormitory', capacity: 6 },
  { value: 'suite', label: 'Suite', capacity: 2 },
  { value: 'apartment', label: 'Apartment', capacity: 4 },
  { value: 'vip', label: 'VIP Suite', capacity: 2 }
];

const ROOM_AMENITIES = [
  'AC', 'TV', 'WiFi', 'Bathroom', 'Balcony', 'Mini Fridge', 'Safe', 'Desk'
];

const statusConfig = {
  ready: { color: 'bg-green-500', icon: CheckCircle, label: 'Ready' },
  occupied: { color: 'bg-blue-500', icon: Users, label: 'Occupied' },
  dirty: { color: 'bg-yellow-500', icon: AlertTriangle, label: 'Dirty' },
  maintenance: { color: 'bg-red-500', icon: Wrench, label: 'Maintenance' },
  blocked: { color: 'bg-gray-500', icon: Ban, label: 'Blocked' }
};

const RoomsManagement = () => {
  const queryClient = useQueryClient();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [filterBuilding, setFilterBuilding] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const token = getToken();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Fetch buildings
  const { data: buildings = [] } = useQuery({
    queryKey: ['accommodation-buildings'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/accommodation/buildings`, { headers });
      if (!res.ok) return [];
      return res.json();
    }
  });

  // Fetch rooms
  const { data: rooms = [], isLoading } = useQuery({
    queryKey: ['accommodation-rooms', filterBuilding, filterStatus],
    queryFn: async () => {
      let url = `${API_URL}/api/admin/accommodation/rooms`;
      const params = new URLSearchParams();
      if (filterBuilding !== 'all') params.append('building_id', filterBuilding);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (params.toString()) url += `?${params.toString()}`;
      
      const res = await fetch(url, { headers });
      if (!res.ok) return [];
      return res.json();
    }
  });

  // Create room
  const createMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/api/admin/accommodation/rooms`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to create room');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['accommodation-rooms']);
      queryClient.invalidateQueries(['accommodation-buildings']);
      queryClient.invalidateQueries(['accommodation-stats']);
      setShowAddDialog(false);
      toast.success('Room created');
    },
    onError: (error) => toast.error(error.message)
  });

  // Update room
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const res = await fetch(`${API_URL}/api/admin/accommodation/rooms/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const detail = body.detail;
        if (detail && typeof detail === 'object' && detail.message) {
          const err = new Error(detail.message);
          err.detail = detail;
          throw err;
        }
        throw new Error(detail || 'Failed to update room');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['accommodation-rooms']);
      queryClient.invalidateQueries(['accommodation-buildings']);
      queryClient.invalidateQueries(['accommodation-stats']);
      queryClient.invalidateQueries(['room-occupancy']);
      queryClient.invalidateQueries(['room-capacity-history']);
      setEditingRoom(null);
      toast.success('Room updated');
    },
    onError: (error) => toast.error(error.message)
  });

  // Update room status
  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }) => {
      const res = await fetch(`${API_URL}/api/admin/accommodation/rooms/${id}/status`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ status })
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to update status');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['accommodation-rooms']);
      queryClient.invalidateQueries(['accommodation-buildings']);
      toast.success('Status updated');
    },
    onError: (error) => toast.error(error.message)
  });

  // Delete room
  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const res = await fetch(`${API_URL}/api/admin/accommodation/rooms/${id}`, {
        method: 'DELETE',
        headers
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to delete room');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['accommodation-rooms']);
      queryClient.invalidateQueries(['accommodation-buildings']);
      toast.success('Room deleted');
    },
    onError: (error) => toast.error(error.message)
  });

  const filteredRooms = rooms.filter(room => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        room.room_number?.toLowerCase().includes(search) ||
        room.building_name?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const RoomForm = ({ room, onSubmit, onCancel, isLoading }) => {
    const isEdit = !!room;
    const originalCapacity = room?.capacity || 1;
    const [formData, setFormData] = useState({
      building_id: room?.building_id || '',
      room_number: room?.room_number || '',
      floor: room?.floor || 1,
      room_type: room?.room_type || 'single',
      capacity: room?.capacity || 1,
      amenities: room?.amenities || [],
      price_per_night: room?.price_per_night || 0,
      notes: room?.notes || '',
      capacity_change_reason: ''
    });

    // Live occupancy for the room being edited
    const { data: occupancy } = useQuery({
      queryKey: ['room-occupancy', room?.id],
      queryFn: async () => {
        const res = await fetch(`${API_URL}/api/admin/accommodation/rooms/${room.id}/occupancy`, { headers });
        if (!res.ok) return null;
        return res.json();
      },
      enabled: !!room?.id
    });

    // Capacity history for the room being edited
    const [showHistory, setShowHistory] = useState(false);
    const { data: historyData } = useQuery({
      queryKey: ['room-capacity-history', room?.id],
      queryFn: async () => {
        const res = await fetch(`${API_URL}/api/admin/accommodation/rooms/${room.id}/capacity-history`, { headers });
        if (!res.ok) return { history: [] };
        return res.json();
      },
      enabled: !!room?.id && showHistory
    });

    const toggleAmenity = (amenity) => {
      setFormData(prev => ({
        ...prev,
        amenities: prev.amenities.includes(amenity)
          ? prev.amenities.filter(a => a !== amenity)
          : [...prev.amenities, amenity]
      }));
    };

    const capacityChanged = isEdit && Number(formData.capacity) !== Number(originalCapacity);
    const reducingBelowOccupancy = isEdit && occupancy && Number(formData.capacity) < Number(occupancy.occupied || 0);

    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(formData);
        }}
        className="space-y-4"
      >
        <div>
          <label className="text-sm font-medium">Building *</label>
          <Select
            value={formData.building_id}
            onValueChange={(value) => setFormData({ ...formData, building_id: value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select building" />
            </SelectTrigger>
            <SelectContent>
              {buildings.map((building) => (
                <SelectItem key={building.id} value={building.id}>
                  {building.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Room Number *</label>
            <Input
              value={formData.room_number}
              onChange={(e) => setFormData({ ...formData, room_number: e.target.value })}
              required
              placeholder="e.g., 101"
              data-testid="input-room-number"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Floor</label>
            <Input
              type="number"
              min="1"
              value={formData.floor}
              onChange={(e) => setFormData({ ...formData, floor: parseInt(e.target.value) || 1 })}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Room Type</label>
            <Select
              value={formData.room_type}
              onValueChange={(value) => {
                // Only auto-fill capacity for NEW rooms. On edit, preserve admin-entered capacity.
                const type = ROOM_TYPES.find(t => t.value === value);
                setFormData(prev => ({
                  ...prev,
                  room_type: value,
                  capacity: isEdit ? prev.capacity : (type?.capacity || prev.capacity)
                }));
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROOM_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label} (up to {type.capacity})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium">Capacity (Beds / Units)</label>
            <Input
              type="number"
              min="1"
              max="500"
              value={formData.capacity}
              onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) || 1 })}
              data-testid="input-room-capacity"
            />
            {isEdit && occupancy && (
              <p className="text-xs text-gray-500 mt-1">
                Currently: <span className="font-medium">{occupancy.occupied}</span> occupied · <span className="font-medium">{occupancy.available}</span> available
                {' '}<button type="button" onClick={() => setShowHistory(v => !v)} className="text-blue-600 underline ml-1">
                  {showHistory ? 'Hide history' : 'View history'}
                </button>
              </p>
            )}
            {reducingBelowOccupancy && (
              <p className="text-xs text-red-600 mt-1">
                Cannot reduce capacity below the {occupancy.occupied} current occupant(s). Reassign delegates first.
              </p>
            )}
          </div>
        </div>

        {isEdit && capacityChanged && (
          <div>
            <label className="text-sm font-medium">Reason for capacity change</label>
            <Input
              value={formData.capacity_change_reason}
              onChange={(e) => setFormData({ ...formData, capacity_change_reason: e.target.value })}
              placeholder="e.g., Added 8 extra beds for conference overflow"
              data-testid="input-capacity-reason"
            />
            <p className="text-xs text-gray-500 mt-1">Recorded in the audit trail.</p>
          </div>
        )}

        {isEdit && showHistory && (
          <div className="border rounded-md p-3 bg-slate-50 max-h-56 overflow-auto">
            <div className="text-sm font-medium mb-2">Capacity change history</div>
            {(historyData?.history || []).length === 0 ? (
              <p className="text-xs text-gray-500">No capacity changes recorded yet.</p>
            ) : (
              <ul className="space-y-2 text-xs">
                {(historyData?.history || []).map((h, idx) => (
                  <li key={idx} className="flex justify-between gap-4 border-b border-slate-200 pb-1">
                    <div>
                      <span className="font-medium">{h.previous_capacity} → {h.new_capacity}</span>
                      {h.reason ? <span className="text-gray-600"> · {h.reason}</span> : null}
                    </div>
                    <div className="text-gray-500">
                      {h.changed_by_email || h.changed_by} · {new Date(h.changed_at).toLocaleString()}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div>
          <label className="text-sm font-medium">Price per Night</label>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={formData.price_per_night}
            onChange={(e) => setFormData({ ...formData, price_per_night: parseFloat(e.target.value) || 0 })}
            placeholder="0.00"
          />
        </div>

        <div>
          <label className="text-sm font-medium mb-2 block">Amenities</label>
          <div className="flex flex-wrap gap-2">
            {ROOM_AMENITIES.map((amenity) => (
              <Badge
                key={amenity}
                variant={formData.amenities.includes(amenity) ? 'default' : 'outline'}
                className="cursor-pointer select-none hover:bg-slate-100 hover:text-slate-900 active:scale-95 transition-all px-3 py-1"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleAmenity(amenity); }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleAmenity(amenity); } }}
                data-testid={`amenity-${amenity.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {amenity}
              </Badge>
            ))}
          </div>
        </div>

        <div>
          <label className="text-sm font-medium">Notes</label>
          <Textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Additional notes about this room"
            rows={2}
          />
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
          <Button type="submit" disabled={isLoading || !formData.building_id} data-testid="submit-room-btn">
            {isLoading ? 'Saving...' : room ? 'Update Room' : 'Create Room'}
          </Button>
        </div>
      </form>
    );
  };

  return (
    <div className="space-y-6" data-testid="rooms-management">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Rooms</h2>
          <p className="text-sm text-gray-500">Manage accommodation rooms and availability</p>
        </div>
        <Button 
          onClick={() => setShowAddDialog(true)} 
          disabled={buildings.length === 0}
          data-testid="add-room-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Room
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search rooms..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={filterBuilding} onValueChange={setFilterBuilding}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="All Buildings" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Buildings</SelectItem>
            {buildings.map((building) => (
              <SelectItem key={building.id} value={building.id}>
                {building.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {Object.entries(statusConfig).map(([key, config]) => (
              <SelectItem key={key} value={key}>
                {config.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {buildings.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <BedDouble className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium mb-2">No buildings configured</h3>
            <p className="text-sm text-gray-500 mb-4">
              Add a building first before creating rooms
            </p>
          </CardContent>
        </Card>
      ) : isLoading ? (
        <div className="text-center py-8 text-gray-500">Loading rooms...</div>
      ) : filteredRooms.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <BedDouble className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium mb-2">No rooms found</h3>
            <p className="text-sm text-gray-500 mb-4">
              {searchTerm || filterStatus !== 'all' || filterBuilding !== 'all'
                ? 'Try adjusting your filters'
                : 'Add your first room to get started'}
            </p>
            {!searchTerm && filterStatus === 'all' && filterBuilding === 'all' && (
              <Button onClick={() => setShowAddDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add First Room
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {filteredRooms.map((room) => {
            const status = statusConfig[room.status] || statusConfig.ready;
            const StatusIcon = status.icon;
            
            return (
              <Card 
                key={room.id} 
                className={`cursor-pointer hover:shadow-md transition-shadow ${
                  room.status === 'occupied' ? 'ring-2 ring-blue-200' : ''
                }`}
                data-testid={`room-card-${room.id}`}
              >
                <CardContent className="p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-lg">{room.room_number}</span>
                    <div className={`p-1 rounded-full ${status.color} text-white`}>
                      <StatusIcon className="h-3 w-3" />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mb-1">{room.building_name}</p>
                  <p className="text-xs text-gray-400 capitalize">{room.room_type}</p>
                  
                  {room.current_guest && (
                    <div className="mt-2 pt-2 border-t">
                      <p className="text-xs font-medium truncate">{room.current_guest.name}</p>
                      <p className="text-xs text-gray-400">
                        Out: {new Date(room.current_guest.check_out_date).toLocaleDateString()}
                      </p>
                    </div>
                  )}

                  {/* Quick Status Change */}
                  {room.status !== 'occupied' && (
                    <div className="flex gap-1 mt-2 pt-2 border-t">
                      {room.status !== 'ready' && (
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="flex-1 h-7 text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            updateStatusMutation.mutate({ id: room.id, status: 'ready' });
                          }}
                        >
                          Ready
                        </Button>
                      )}
                      {room.status !== 'dirty' && (
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="flex-1 h-7 text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            updateStatusMutation.mutate({ id: room.id, status: 'dirty' });
                          }}
                        >
                          Dirty
                        </Button>
                      )}
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="h-7"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingRoom(room);
                        }}
                      >
                        <Edit2 className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add Room Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Room</DialogTitle>
          </DialogHeader>
          <RoomForm
            onSubmit={(data) => createMutation.mutate(data)}
            onCancel={() => setShowAddDialog(false)}
            isLoading={createMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Room Dialog */}
      <Dialog open={!!editingRoom} onOpenChange={(open) => !open && setEditingRoom(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Room {editingRoom?.room_number}</DialogTitle>
          </DialogHeader>
          {editingRoom && (
            <RoomForm
              room={editingRoom}
              onSubmit={(data) => updateMutation.mutate({ id: editingRoom.id, data })}
              onCancel={() => setEditingRoom(null)}
              isLoading={updateMutation.isPending}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RoomsManagement;
